import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { messages } from "../resources/messages";

/**
 * Finds and displays the error message associated with a given form control or group.
 *
 * Rules:
 * - Won't display mesages for controls that have not been touched
 * - Will only ever display one message at a time
 */
@Component({
    selector: "app-validation-message",
    template: `{{ message }}`,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValidationMessageComponent {
    @Input() public control: AbstractControl;
    @Input() public touched: boolean;
    @Input() public errors: Record<string, any>;
    @Input() public controls: FormGroup;

    get message() {
        if (this.control) {
            let controls = null;

            if (this.control instanceof FormGroup && this.control.controls) {
                controls = this.control.controls;
            }
            return this.getMessage(this.control.touched, this.control.errors, controls);
        }

        return this.getMessage(this.touched, this.errors, this.controls);
    }

    // TODO: fix this for built-in Angular validators like minlength
    private getMessage(touched: boolean, errors: Record<string, any>, controls: FormGroup): any | void {
        if (touched) {
            if (errors) {
                for (const errorName in errors) {
                    if (errors.hasOwnProperty(errorName)) {
                        return this.generateValidationMessage(errors, errorName);
                    }
                }
            }

            if (controls) {
                for (const controlName in controls) {
                    if (controls.hasOwnProperty(controlName)) {
                        const error = this.getMessage(
                            controls[controlName].touched,
                            controls[controlName].errors,
                            controls[controlName].controls
                        );
                        if (error) {
                            return error;
                        }
                    }
                }
            }
        }
    }

    private generateValidationMessage(errors: Record<string, any>, errorName: string) {
        if (errorName === "required") {
            return messages.validation.required;
        }

        if (errorName === "pattern") {
            return messages.validation.pattern;
        }

        if (errorName === "maxlength") {
            const { requiredLength } = errors[errorName];
            return `Please enter no more than ${requiredLength} characters`;
        }

        if (errorName === "minlength") {
            const { requiredLength } = errors[errorName];
            return `Please enter at least ${requiredLength} characters`;
        }

        return errors[errorName];
    }
}
